
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface SaveGamePlanDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (name: string, description: string) => void;
  initialName?: string;
  initialDescription?: string;
  isTemplate?: boolean;
}

export function SaveGamePlanDialog({ 
  open, 
  onCancel, 
  onSave, 
  initialName = '', 
  initialDescription = '',
  isTemplate = false
}: SaveGamePlanDialogProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  // Update the name and description when initialName or initialDescription change
  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [open, initialName, initialDescription]);

  const handleSave = () => {
    if (!name.trim()) {
      return; // Don't save if name is empty
    }
    onSave(name, description);
    // Reset will be done through effect when dialog closes
  };

  const handleCancel = () => {
    // Reset form and close
    setName(initialName);
    setDescription(initialDescription);
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isTemplate ? 'Create New Game Plan from Template' : 'Save Game Plan'}
          </DialogTitle>
        </DialogHeader>

        {isTemplate && (
          <div className="bg-blue-50 text-blue-700 p-3 rounded mb-2 text-sm">
            You're creating a new game plan based on a template. 
            Please provide a name and description for your new game plan.
          </div>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="Enter game plan name" 
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Add a description (optional)" 
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          <Button type="submit" onClick={handleSave} disabled={!name.trim()}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
